/**
 * Module: lib/copilot/CopilotProvider
 * App-shell-level React context. Owns a single useChat handle so
 * conversation + stream state survive page navigation. Lazy-init:
 * does nothing until the widget opens or /home mounts.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { createContext } from 'use-context-selector'
import Cookies from 'js-cookie'
import { useChat } from './hooks/useChat'
import {
  fetchConversation,
  useConversationList,
  messagesToTranscript,
  extractTotalTokens
} from './hooks/useConversations'
import { constants } from '../utils/constants'
import { refreshTokenDeduped } from './sharedRefresh'

const BASE = process.env.NEXT_PUBLIC_COPILOT_API_BASE ?? '/copilot-api'
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export interface CopilotContextValue {
  readonly widgetOpen: boolean
  readonly setWidgetOpen: (open: boolean) => void
  /** Home (/home) chat shell: hero vs in-thread view. Survives route changes; reset only via back/new chat. */
  readonly homeInChatView: boolean
  readonly setHomeInChatView: (inChatView: boolean) => void
  readonly chat: ReturnType<typeof useChat>
  readonly initialised: boolean
}

export const CopilotContext = createContext<CopilotContextValue | null>(null)

export function CopilotProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [widgetOpen, setWidgetOpen] = useState(false)
  const [initialised, setInitialised] = useState(false)
  const [homeInChatView, setHomeInChatView] = useState(false)
  const chat = useChat({
    baseUrl: BASE,
    getAccessToken: () => Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '',
    // On a 401 from copilot endpoints, force-refresh the merchant JWT via
    // /merchant-user/refresh. We go through `refreshTokenDeduped` (not
    // refreshExpiredIdToken directly) so parallel 401s on the dashboard mount
    // (suggestions + conversations + chat) coalesce onto ONE refresh attempt
    // — otherwise rotating the auth cookie from multiple in-flight refreshes
    // races and can force a spurious logout. The deduped helper resolves to
    // the same boolean as the underlying call; the retry inside CopilotClient
    // then re-reads the rotated cookie via the same getAccessToken callback.
    onUnauthorized: async () => {
      await refreshTokenDeduped()
    }
  })
  const { loadConversation } = chat
  // Only fetch the list once the widget is actually opened — most sessions never do,
  // and this hook runs at app-shell level on every dashboard page load.
  const { data: conversations } = useConversationList({ enabled: widgetOpen })

  // Latest chat state for the async hydration guard below, without making the
  // effect re-subscribe on every streamed token.
  const chatRef = useRef(chat)
  useEffect(() => {
    chatRef.current = chat
  }, [chat])

  // Hydrate widget chat from the most recent server conversation once per open
  // (stable `loadConversation` avoids re-subscribing when unrelated chat fields update).
  useEffect(() => {
    if (!widgetOpen || initialised) return
    // Wait for the list query to resolve — latching `initialised` while the data
    // is still undefined would skip history hydration for the provider's lifetime.
    if (conversations === undefined) return
    setInitialised(true)
    if (conversations.length === 0) return
    const top = conversations[0]
    const ageMs = Date.now() - new Date(top.updated_at).getTime()
    if (ageMs > SEVEN_DAYS_MS) return
    void (async () => {
      try {
        const detail = await fetchConversation(top.id)
        // A turn the user started while this fetch was in flight wins over replayed
        // history — replacing the transcript here would drop their message mid-stream.
        if (chatRef.current.status !== 'idle' || chatRef.current.transcript.length > 0) return
        loadConversation(top.id, messagesToTranscript(detail.messages), extractTotalTokens(detail), detail.plans)
      } catch {
        // fall back to the fresh conversation already in chat state
      }
    })()
  }, [widgetOpen, initialised, conversations, loadConversation])

  const value = useMemo(
    () => ({ widgetOpen, setWidgetOpen, homeInChatView, setHomeInChatView, chat, initialised }),
    [widgetOpen, homeInChatView, chat, initialised]
  )

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
}
